<template>
  <div>
    <div class="flex justify-center space-x-2">
      <div
        v-tooltip="modelValue ?? ''"
        class="max-w-50 cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap"
        data-cy="url-value"
        :class="
          shownValue
            ? 'min-w-20 text-primary'
            : 'flex h-6-5 w-14 items-center justify-center rounded-full bg-disabled'
        "
        @click="openLink"
      >
        {{ shownValue || "-" }}
      </div>
      <button
        v-if="
          (attrs.model as IPlacementTableRow)?.status !==
          approvedPlacementStatus
        "
        data-cy="open-modal-button"
        @click.stop="showEditMenu = true"
      >
        <icon-base
          v-tooltip="editIconTooltip"
          class="focus:outline-hidden"
          :icon="IconEdit"
        />
      </button>
    </div>
    <div
      v-if="showEditMenu"
      ref="editUrlElement"
      v-on-click-outside="() => (showEditMenu = false)"
      data-cy="edit-modal"
      class="absolute z-50 w-80 rounded-sm border border-primary bg-white p-1 shadow-xs"
    >
      <div class="mx-2">
        <textarea
          ref="urlInput"
          v-model="localUrl"
          data-cy="edit-modal-input"
          class="w-full resize-none overflow-hidden rounded-sm border border-gray-200 p-1 text-headline placeholder:text-disabled-medium focus:outline-hidden"
          :placeholder="$t('COMMON.URL')"
          :rows="1"
          @input="resetUrlInputHeight"
        />
      </div>
      <modal-buttons
        class="justify-end border-gray-200 bg-white py-5"
        :save-button-text="$t('COMMON.UPDATE')"
        :on-cancel="() => (showEditMenu = false)"
        :on-save="updateUrl"
        :disabled="!localUrl || loading"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useAttrs, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { validateString } from "@/helpers/common";
import { usePromiseWrapper } from "@/hooks/common";
import { useTeleportV2 } from "@/hooks/elements";

import IconEdit from "@/components/icons/IconEdit.vue";
import IconBase from "@/components/ui/IconBase.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";

import type { IPlacementTableRow } from "@/models/offers";

const { dispatch } = useStore();
const attrs = useAttrs();

const { t } = useI18n();

const { modelValue } = defineProps<{
  modelValue?: string;
}>();

const approvedPlacementStatus = 3;

const editUrlElement = ref<HTMLDivElement | null>(null);
const urlInput = ref<HTMLTextAreaElement | null>(null);

const showEditMenu = ref(false);

useTeleportV2({
  target: editUrlElement,
  to: "#modals",
  offset: { top: 5, left: -150 },
});

const localUrl = ref(modelValue);

const shownValue = computed(() => validateString(modelValue));

const editIconTooltip = computed(() =>
  modelValue
    ? `${t("COMMON.EDIT")} ${t("COMMON.URL")}`
    : `${t("COMMON.ADD")} ${t("COMMON.URL")}`
);

const resetUrlInputHeight = () => {
  const el = urlInput.value;

  if (!el) {
    return;
  }

  const textScrollHeight = `${el.scrollHeight}px`;

  if (textScrollHeight === el.style.height) {
    return;
  }

  el.style.height = "auto"; // reset height
  el.style.height = textScrollHeight; // set to content height
};

const openLink = () => {
  if (!modelValue) {
    return;
  }

  window.open(modelValue, "_blank");
};

const { fetchWrapper: updateUrl, loading } = usePromiseWrapper(async () => {
  if (!attrs.model) {
    return;
  }

  await dispatch("applications/updatePlacement", {
    ...(attrs.model as IPlacementTableRow),
    url: localUrl.value?.trim(),
  });

  showEditMenu.value = false;
});

watch(
  () => modelValue,
  (newValue) => {
    localUrl.value = newValue;
  }
);

watch(showEditMenu, (newValue) => {
  if (!newValue) {
    localUrl.value = modelValue;
  }
});

watch(urlInput, async (el) => {
  if (!el) {
    return;
  }
  resetUrlInputHeight();
  await nextTick();
  el.focus();
});
</script>
